import { useEffect, useMemo, useRef } from "react";
import { useAuth } from "@/context/AuthContext";

declare global {
  interface Window {
    $_Huggy?: {
      defaultCountry: string;
      uuid: string;
      company: string;
      name?: string;
      email?: string;
      phone?: string;
      channelGreeting?: string;
      sessionId?: string;
      timestamp?: number;
      [key: string]: any; // Para permitir propriedades dinâmicas
    };
    pwz?: {
      context: {
        id: string;
      };
    };
  }
}

interface HuggyWidgetProps {
  enabled?: boolean;
}

const HuggyWidget = ({ enabled = false }: HuggyWidgetProps) => {
  const { user } = useAuth();
  const isInitializedRef = useRef(false);
  
  // Memoizar propriedades relevantes do usuário para evitar re-renders desnecessários
  const userProps = useMemo(() => {
    if (!user) return null;
    return {
      id: user.id,
      name: user.name,
      email: user.email,
      phone: user.phone
    };
  }, [user?.id, user?.name, user?.email, user?.phone]);
  
  useEffect(() => {
    if (!enabled) return;

    // FUNÇÃO DE LIMPEZA COMPLETA PARA LOGOUT
    const completeHuggyCleanup = () => {
      console.log("🧹 HUGGY WIDGET - LIMPEZA COMPLETA DO HUGGY");
      
      // Remover todos os scripts do Huggy
      const existingScripts = document.querySelectorAll('script[src*="huggy"], script[src*="pwz"], script[src*="widget.min.js"]');
      existingScripts.forEach(script => {
        console.log("🗑️ Widget removendo script:", (script as HTMLScriptElement).src);
        script.remove();
      });
      
      // Remover TODOS os elementos DOM relacionados ao Huggy
      const huggySelectors = [
        '[id*="huggy"]', '[class*="huggy"]', 'iframe[src*="huggy"]', 
        '[id*="pwz"]', '[class*="pwz"]', '[data-huggy]',
        '.huggy-widget', '#huggy-chat', '.huggy-launcher'
      ];
      
      huggySelectors.forEach(selector => {
        const elements = document.querySelectorAll(selector);
        elements.forEach(el => {
          console.log("🗑️ Widget removendo elemento:", el.tagName, el.className || el.id);
          el.remove();
        });
      });
      
      // Limpar variáveis globais COMPLETAMENTE
      if (window.$_Huggy) {
        console.log("🗑️ Widget limpando $_Huggy:", window.$_Huggy);
        delete window.$_Huggy;
      }
      if (window.pwz) {
        console.log("🗑️ Widget limpando pwz:", window.pwz);
        delete window.pwz;
      }
      
      console.log("✅ HUGGY WIDGET - LIMPEZA COMPLETA FINALIZADA");
    };

    // SE USUÁRIO FEZ LOGOUT (user === null), fazer limpeza completa e sair
    if (user === null) {
      completeHuggyCleanup();
      isInitializedRef.current = false;
      return;
    }

    // Se já foi inicializado com os mesmos dados, não fazer nada
    if (isInitializedRef.current && userProps) {
      console.log("🚫 Huggy já inicializado com os mesmos dados, evitando re-creation");
      return;
    }

    // LIMPAR TUDO AGRESSIVAMENTE ANTES DE RECONFIGURAR
    const existingScripts = document.querySelectorAll('script[src*="huggy"], script[src*="pwz"]');
    existingScripts.forEach(script => {
      console.log("🗑️ Removendo script:", (script as HTMLScriptElement).src);
      script.remove();
    });
    
    // Limpar elementos do DOM
    const huggyElements = document.querySelectorAll('[id*="huggy"], [class*="huggy"], iframe[src*="huggy"]');
    huggyElements.forEach(el => {
      console.log("🗑️ Removendo elemento Huggy:", el);
      el.remove();
    });
    
    // Limpar variáveis globais COMPLETAMENTE
    if (window.$_Huggy) {
      console.log("🗑️ Limpando $_Huggy anterior:", window.$_Huggy);
      delete window.$_Huggy;
    }
    if (window.pwz) {
      console.log("🗑️ Limpando pwz anterior:", window.pwz);
      delete window.pwz;
    }

    // AGUARDAR E RECONFIGURAR DO ZERO (APENAS SE HOUVER USUÁRIO)
    setTimeout(() => {
      console.log("🔄 RECONFIGURANDO HUGGY DO ZERO...");
      
      // VERIFICAR SE AINDA EXISTEM DADOS RESIDUAIS ANTES DE RECONFIGURAR
      console.log("🔍 VERIFICANDO DADOS RESIDUAIS ANTES DE RECONFIGURAR:");
      console.log("localStorage keys:", Object.keys(localStorage).filter(key => 
        key.toLowerCase().includes('huggy') || key.toLowerCase().includes('pwz')
      ));
      console.log("sessionStorage keys:", Object.keys(sessionStorage).filter(key => 
        key.toLowerCase().includes('huggy') || key.toLowerCase().includes('pwz')
      ));
      console.log("Cookies:", document.cookie.split(';').filter(cookie => 
        cookie.toLowerCase().includes('huggy') || cookie.toLowerCase().includes('pwz')
      ));
      
      // Obter nome da plataforma do ambiente
      const platformName = import.meta.env.VITE_PLATFORM_NAME;
      const channelGreeting = platformName ? `Bem-vindo ao atendimento do ${platformName}` : undefined;
      
      console.log("🏷️ Plataforma identificada:", platformName);
      console.log("💬 Saudação configurada:", channelGreeting);
      
      // FORÇAR UM ID ÚNICO PARA CADA SESSÃO (baseado no user.id + timestamp)
      const sessionId = userProps ? `${userProps.id}_${Date.now()}` : `guest_${Date.now()}`;
      console.log("🆔 Session ID único gerado:", sessionId);
      
      // Configurar $_Huggy FRESH com ID único
      window.$_Huggy = { 
        defaultCountry: '+55', 
        uuid: 'a59a98bd-405e-4719-a01f-d1f835bdf142', 
        company: '366955',
        // Adicionar parâmetros que podem forçar nova sessão
        sessionId: sessionId,
        timestamp: Date.now()
      };

      // Adicionar channelGreeting se plataforma estiver definida
      if (channelGreeting) {
        window.$_Huggy.channelGreeting = channelGreeting;
      }

      // Adicionar dados do usuário autenticado DIRETAMENTE conforme documentação oficial
      if (userProps) {
        console.log("🔍 DEBUG - Usuário logado:", userProps);
        
        // Configurar propriedades DIRETAMENTE no $_Huggy conforme documentação
        window.$_Huggy.name = userProps.name;
        window.$_Huggy.email = userProps.email;
        window.$_Huggy.phone = userProps.phone?.replace(/\D/g, '') || undefined;
        
        console.log("📤 DEBUG - Dados enviados para Huggy:", {
          name: window.$_Huggy.name,
          email: window.$_Huggy.email,
          phone: window.$_Huggy.phone,
          sessionId: window.$_Huggy.sessionId,
          timestamp: window.$_Huggy.timestamp
        });
      }

      // FORÇAR LIMPEZA DE TODOS OS CACHES ANTES DE CARREGAR O SCRIPT
      if ('caches' in window) {
        caches.keys().then(cacheNames => {
          cacheNames.forEach(cacheName => {
            if (cacheName.toLowerCase().includes('huggy') || 
                cacheName.toLowerCase().includes('pwz') ||
                cacheName.toLowerCase().includes('widget')) {
              console.log("🗑️ FORÇANDO remoção de cache:", cacheName);
              caches.delete(cacheName);
            }
          });
        });
      }

      // Script oficial da Huggy FRESH com parâmetro anti-cache
      const scriptSrc = `https://js.huggy.chat/widget.min.js?t=${Date.now()}&session=${sessionId}`;
      console.log("📜 Carregando script Huggy com anti-cache:", scriptSrc);
      
      (function(i: any, s: any, o: any, g: any, r: any, a: any, m: any) { 
        i[r] = { context: { id: 'da47b8c1926038ed602a4d59700b76a3' } };
        a = o; 
        o = s.createElement(o); 
        o.async = 1; 
        o.src = g; 
        m = s.getElementsByTagName(a)[0]; 
        m.parentNode.insertBefore(o, m); 
      })(window, document, 'script', scriptSrc, 'pwz', undefined, undefined);

      // Marcar como inicializado
      isInitializedRef.current = true;
      console.log("✅ HUGGY RECONFIGURADO COM NOVA SESSÃO!");
    }, 500);

    // Limpeza no cleanup do useEffect
    return () => {
      const script = document.querySelector('script[src*="huggy.chat"]');
      if (script) script.remove();
    };
  }, [enabled, userProps]); // Usar userProps memoizado em vez de user diretamente

  if (!enabled) return null;
  return null;
};

export default HuggyWidget;